<template>
  <div>
    <page-header :title="$t('applicationWindow.create')" :desc="model.name">
      <li class="breadcrumb-item">
        <router-link :to="{name:'applicationWindows.index'}">{{ $tc('applicationWindow.name', 2) }}</router-link>
      </li>
      <li class="breadcrumb-item">{{ $t('action.createNew') }}</li>
    </page-header>
    <page-content>
      <div class="wizard-steps">
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="wizard-step"
          :class="{ 'active': currentStep === index, 'completed': currentStep > index }"
          @click="goToStep(index)"
        >
          <div class="wizard-step-circle">
            <i v-if="currentStep > index" class="fa fa-check"></i>
            <span v-else>{{ index + 1 }}</span>
          </div>
          <div class="wizard-step-label">{{ step.label }}</div>
        </div>
        <div class="wizard-line"></div>
      </div>

      <ntm-block :collapsable="false">
        <template v-if="steps[currentStep] && steps[currentStep].type === 'basic'">
          <formly-form :form="form" :model="model" :fields="basicFieldsBeforeCategories"></formly-form>

          <label class="mb-0">{{ $tc('category.name', 2) }}</label>
          <categories-manager v-model="model.categories" class="mb-3" />
          <span v-if="categoriesErrorMessage" class="help-block form-text text-danger">{{ categoriesErrorMessage }}</span>

          <formly-form :form="form" :model="model" :fields="basicFieldsUntilPlayerTags"></formly-form>

          <player-tags-select v-if="model.collectPlayerTags" v-model="model.playerTagUuids" :errors="form.$errors" />

          <formly-form :form="form" :model="model" :fields="basicFieldsAfterPlayerTags"></formly-form>
        </template>
        <formly-form v-else :form="form" :model="model" :fields="currentFields"></formly-form>

        <template v-if="steps[currentStep] && steps[currentStep].type === 'team'">
          <hr>
          <h5 class="mb-3">{{ $t('customField.title') }}</h5>
          <custom-fields-manager v-model="model.customFields" application-type="TEAM" />
        </template>

        <template v-if="steps[currentStep] && steps[currentStep].type === 'freeAgent'">
          <hr>
          <h5 class="mb-3">{{ $t('customField.title') }}</h5>
          <custom-fields-manager v-model="model.customFields" application-type="FREE_AGENT" />
        </template>

        <div class="d-flex justify-content-between mt-4 mb-3">
          <button
            v-if="currentStep > 0"
            type="button"
            class="btn btn-light"
            @click="prevStep"
          >
            <i class="fa fa-arrow-left mr-1"></i>{{ $t('action.back') }}
          </button>
          <div v-else></div>

          <div>
            <button
              v-if="currentStep < steps.length - 1"
              type="button"
              class="btn btn-primary"
              @click="nextStep"
            >
              {{ $t('action.next') }}<i class="fa fa-arrow-right ml-1"></i>
            </button>
            <button
              v-else
              type="button"
              class="btn btn-success"
              @click="onSubmit"
            >
              {{ $t('save') }}
            </button>
          </div>
        </div>
      </ntm-block>
    </page-content>
  </div>
</template>

<script>
import fieldsStep1 from './formDefinitionStep1'
import fieldsStep2 from './formDefinitionStep2'
import fieldsStep3 from './formDefinitionStep3'
import ApplicationWindowService from '@/services/applicationWindow.service'
import CustomFieldsManager from './components/CustomFieldsManager.vue'
import CategoriesManager from './components/CategoriesManager.vue'
import PlayerTagsSelect from './components/PlayerTagsSelect.vue'
import TeamService from '@/services/team.service'
import router from '@/router'

export default {
  components: { CustomFieldsManager, CategoriesManager, PlayerTagsSelect },
  data () {
    return {
      currentStep: 0,
      form: {},
      model: {
        teamApplicationEnabled: true,
        freeAgentApplicationEnabled: false,
        categories: [],
        isCoachRequired: false,
        hasFullDateOfBirth: false,
        hasRequiredPlayerIdentificationNumber: false,
        collectPlayerPositions: false,
        collectPlayerTags: false,
        playerTagUuids: [],
        availablePlayerPositions: [],
        customFields: []
      }
    }
  },
  created () {
    TeamService.getPlayerPositions().then((res) => {
      this.model.availablePlayerPositions = res.data || []
    })
  },
  computed: {
    steps () {
      const result = [
        { label: this.$t('applicationWindow.stepBasic'), fields: fieldsStep1, type: 'basic' }
      ]
      if (this.model.teamApplicationEnabled) {
        result.push({ label: this.$t('applicationWindow.stepTeam'), fields: fieldsStep2, type: 'team' })
      }
      if (this.model.freeAgentApplicationEnabled) {
        result.push({ label: this.$t('applicationWindow.stepFreeAgent'), fields: fieldsStep3, type: 'freeAgent' })
      }
      return result
    },
    currentFields () {
      return this.steps[this.currentStep].fields
    },
    basicFieldsBeforeCategories () {
      return fieldsStep1.filter(f => f.key === 'name')
    },
    basicFieldsUntilPlayerTags () {
      const fields = fieldsStep1.filter(f => f.key !== 'name')
      return fields.slice(0, fields.findIndex(f => f.key === 'collectPlayerTags') + 1)
    },
    basicFieldsAfterPlayerTags () {
      const fields = fieldsStep1.filter(f => f.key !== 'name')
      return fields.slice(fields.findIndex(f => f.key === 'collectPlayerTags') + 1)
    },
    categoriesErrorMessage () {
      const errors = this.form.$errors && this.form.$errors.categories
      if (!errors) return null
      const errorKey = Object.keys(errors)[0]
      if (errorKey === undefined || typeof errors[errorKey] === 'boolean') return null
      const raw = errors[errorKey]
      if (raw.includes(' ')) return raw
      const parts = raw.split('.')
      const errorTypeParts = parts[parts.length - 1].split('|')
      const errorTypeName = errorTypeParts.shift()
      return this.$t('validation.categories.' + errorTypeName, errorTypeParts)
    }
  },
  methods: {
    goToStep (index) {
      if (index < this.currentStep) {
        this.currentStep = index
      }
    },
    nextStep () {
      const step = this.steps[this.currentStep]
      let validate = null
      if (step.type === 'basic') validate = () => ApplicationWindowService.validateStep1(this.model)
      if (step.type === 'team') validate = () => ApplicationWindowService.validateStep2(this.model)

      if (!validate) {
        this.currentStep++
        return
      }

      validate().then(() => {
        this.form.$errors = {}
        this.currentStep++
      }).catch((err) => {
        this.form.$errors = err.response.data.errors
      })
    },
    prevStep () {
      if (this.currentStep > 0) {
        this.currentStep--
      }
    },
    goToFirstStepWithError (errors) {
      const errorKeys = Object.keys(errors)
      const stepIndex = this.steps.findIndex(step =>
        step.fields.some(f => errorKeys.includes(f.key)) ||
        (step.type === 'basic' && errorKeys.includes('categories'))
      )
      if (stepIndex !== -1) {
        this.currentStep = stepIndex
      }
    },
    onSubmit () {
      ApplicationWindowService.store(this.model).then(() => {
        setTimeout(() => {
          router.push({ name: 'applicationWindow.index' })
        }, 300)
      }).catch((err) => {
        const errors = err.response.data.errors
        this.form.$errors = errors
        this.goToFirstStepWithError(errors)
      })
    }
  }
}
</script>

<style scoped>
.wizard-steps {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  padding: 0 16px;
}

.wizard-line {
  position: absolute;
  top: 20px;
  left: calc(16px + 40px);
  right: calc(16px + 40px);
  height: 2px;
  background: #e4e7ed;
  z-index: 0;
}

.wizard-step {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: default;
  flex: 1;
}

.wizard-step.completed {
  cursor: pointer;
}

.wizard-step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e4e7ed;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 15px;
  transition: background 0.2s, color 0.2s;
  margin-bottom: 8px;
}

.wizard-step.active .wizard-step-circle {
  background: #008078;
  color: #fff;
}

.wizard-step.completed .wizard-step-circle {
  background: #008078;
  color: #fff;
}

.wizard-step-label {
  font-size: 13px;
  font-weight: 600;
  color: #999;
  text-align: center;
}

.wizard-step.active .wizard-step-label,
.wizard-step.completed .wizard-step-label {
  color: #008078;
}
</style>
